import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import {
  createProduct,
  getProductById,
  updateProduct,
  getUnits,
  getProductAttributes
} from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import { Product, ProductAttribute, ProductVariant, ProductBarcode, Unit } from '../../types/product';
import { Category } from '../../types/category';
import axiosInstance from '../../utils/axiosConfig';

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  // Loading states for different data fetching operations
  const [loading, setLoading] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Product main details
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'discontinued'>('active');
  const [isVariable, setIsVariable] = useState<boolean>(true);
  const [mainImage, setMainImage] = useState('');
  const [images, setImages] = useState<string[]>([]);

  // Product attributes
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');
  const [savedAttributes, setSavedAttributes] = useState<ProductAttribute[]>([]);

  // Product variants
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [expandedVariant, setExpandedVariant] = useState<string | false>(false);
  const [newBarcode, setNewBarcode] = useState('');
  // استخدام خريطة لتخزين الباركودات الإضافية لكل متغير وباركود
  const [additionalBarcodes, setAdditionalBarcodes] = useState<{[key: string]: string}>({});
  
  // Units and Categories
  const [units, setUnits] = useState<Unit[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // Cost History Dialog
  const [costHistoryDialog, setCostHistoryDialog] = useState<{
    open: boolean;
    costHistory: Array<{
      cost: number;
      date: string;
      invoiceId?: string;
      invoiceNumber?: string;
    }>;
    barcodeCode: string;
  }>({
    open: false,
    costHistory: [],
    barcodeCode: ''
  });

  // Initial form loading
  // Staggered loading to prevent overwhelming the server
  useEffect(() => {
    // Clear any previous errors when component mounts or id changes
    setError(null);
    
    // Load the product details first if in edit mode
    if (isEditing && id) {
      fetchProduct(id);
    }
    
    // Set initial data loading state to true
    setLoadingInitialData(true);
    
    // Stagger API calls with small delays to prevent server overload
    const loadData = async () => {
      try {
        await fetchUnits();
        
        // Small delay before fetching categories
        setTimeout(async () => {
          try {
            await fetchCategories();
          } catch (err) {
            console.error('Error in delayed category loading:', err);
          }
        }, 300);
        
        // Further delay before fetching saved attributes
        setTimeout(async () => {
          try {
            await fetchSavedAttributes();
            // All data is loaded, set loading state to false
            setLoadingInitialData(false);
          } catch (err) {
            console.error('Error in delayed attributes loading:', err);
            setLoadingInitialData(false);
          }
        }, 600);
      } catch (err) {
        console.error('Error in initial unit loading:', err);
        setLoadingInitialData(false);
      }
    };
    
    loadData();
  }, [id]);
  
  // Use the productService function instead of direct axios call
  const fetchSavedAttributes = async () => {
    setLoadingAttributes(true);
    try {
      const attributes = await getProductAttributes();
      setSavedAttributes(attributes);
    } catch (err) {
      console.error('Error fetching saved attributes:', err);
      // Don't show error UI for background data loading
    } finally {
      setLoadingAttributes(false);
    }
  };
  
  const fetchUnits = async () => {
    setLoadingUnits(true);
    try {
      const response = await getUnits(1, 100); // Get up to 100 units
      setUnits(response.units);
    } catch (err) {
      console.error('Error fetching units:', err);
      // Don't show error UI for background data loading
    } finally {
      setLoadingUnits(false);
    }
  };

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const categoriesList = await getCategories();
      setCategories(categoriesList);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Don't show error UI for background data loading
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchProduct = async (productId: string) => {
    setLoading(true);
    try {
      const product = await getProductById(productId);
      
      // Set product details in a single batch update
      setName(product.name);
      setDescription(product.description || '');
      setStatus(product.status);
      setMainImage(product.mainImage || '');
      setImages(product.images || []);
      
      // Determine if product is variable based on productType field or fallback to variants count
      if (product.productType) {
        // استخدام حقل productType إذا كان موجوداً
        setIsVariable(product.productType === 'variable');
      } else {
        // استخدام عدد المتغيرات كطريقة احتياطية للمنتجات القديمة
        setIsVariable(product.variants && product.variants.length > 1);
      }
      
      // Set attributes
      setAttributes(product.attributeNames || []);
      
      // Set variants with default values if missing
      const processedVariants = (product.variants || []).map(variant => ({
        ...variant,
        status: variant.status || 'active',
        quantity: variant.quantity || 0,
        unit: variant.unit || '',
        barcodes: Array.isArray(variant.barcodes) ? variant.barcodes : []
      }));
      
      setVariants(processedVariants);
      setError(null);
    } catch (err: any) {
      let errorMessage = 'فشل في تحميل بيانات المنتج';
      
      // Extract more specific error message if available
      if (err?.response?.data?.message) {
        errorMessage += `: ${err.response.data.message}`;
      }
      
      setError(errorMessage);
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  // Attribute handlers
  const handleAddAttribute = () => {
    if (!newAttributeName.trim()) {
      setError('يرجى إدخال اسم الخاصية');
      return;
    }

    // Check if attribute already exists
    if (attributes.some(attr => attr.name === newAttributeName)) {
      setError('هذه الخاصية موجودة بالفعل');
      return;
    }

    const newAttr: ProductAttribute = {
      name: newAttributeName,
      values: newAttributeValue ? [newAttributeValue] : []
    };

    setAttributes([...attributes, newAttr]);
    setNewAttributeName('');
    setNewAttributeValue('');
    setError(null);
  };

  const handleAddAttributeValue = (attrIndex: number, value: string) => {
    // Asegúrate de que value no sea undefined o null y no esté vacío
    if (!value || typeof value !== 'string' || !value.trim()) {
      setError('يرجى إدخال قيمة صالحة');
      return;
    }

    const updatedAttributes = [...attributes];
    const attribute = updatedAttributes[attrIndex];

    // Check if value already exists
    if (attribute.values.includes(value)) {
      setError('هذه القيمة موجودة بالفعل لهذه الخاصية');
      return;
    }

    attribute.values.push(value);
    setAttributes(updatedAttributes);
    setError(null);
  };

  const handleRemoveAttributeValue = (attrIndex: number, valueIndex: number) => {
    const updatedAttributes = [...attributes];
    updatedAttributes[attrIndex].values.splice(valueIndex, 1);
    setAttributes(updatedAttributes);
  };

  const handleRemoveAttribute = (index: number) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  // Variant handlers
  const handleAddVariant = () => {
    // Generate a variant based on all combinations of the first two attributes if possible
    if (attributes.length < 1 || attributes[0].values.length === 0) {
      setError('يجب إضافة خاصية واحدة على الأقل مع قيم لإنشاء متغيرات');
      return;
    }

    // Use the first available unit if there are any, otherwise use an empty string
    const defaultUnit = units.length > 0 ? units[0].name : '';
    
    const newVariant: ProductVariant = {
      serialNumber: variants.length + 1, // إضافة الرقم التسلسلي التلقائي
      attributes: {},
      quantity: 0,
      unit: defaultUnit,
      code: '',
      barcodes: [],
      status: 'active'
    };

    setVariants([...variants, newVariant]);
    setError(null);
  };

  const handleUpdateVariantAttribute = (variantIndex: number, attrName: string, value: string) => {
    const updatedVariants = [...variants];
    updatedVariants[variantIndex].attributes = {
      ...updatedVariants[variantIndex].attributes,
      [attrName]: value
    };
    setVariants(updatedVariants);
  };

  const handleUpdateVariantField = (
    variantIndex: number,
    field: keyof ProductVariant,
    value: any
  ) => {
    const updatedVariants = [...variants];
    
    // Validate and sanitize values based on field type
    if (field === 'quantity' && typeof value === 'string') {
      // Convert to number and ensure it's not negative
      value = Math.max(0, Number(value) || 0);
    } else if (field === 'status' && typeof value === 'string') {
      // Ensure status is one of the allowed values
      if (!['active', 'inactive', 'outOfStock'].includes(value)) {
        value = 'active';
      }
    } else if (field === 'unit' && (!value || typeof value !== 'string')) {
      // Ensure unit has a default value if empty
      value = units.length > 0 ? units[0].name : 'قطعة';
    }
    
    (updatedVariants[variantIndex] as any)[field] = value;
    setVariants(updatedVariants);
  };
  
  // Add field to track the variant's SKU
  const handleUpdateVariantSku = (variantIndex: number, sku: string) => {
    const updatedVariants = [...variants];
    updatedVariants[variantIndex].sku = sku;
    setVariants(updatedVariants);
  };
  
  // إضافة باركود إضافي إلى باركود موجود
  const handleAddAdditionalBarcode = (
    variantIndex: number,
    barcodeIndex: number,
    additionalCode: string
  ) => {
    if (!additionalCode.trim()) {
      setError('يرجى إدخال رمز الباركود الإضافي');
      return;
    }
    
    // التحقق مما إذا كان الباركود موجودًا بالفعل في أي متغير
    const barcodeExists = variants.some(v =>
      v && v.barcodes && (
        v.barcodes.some(b => b.code === additionalCode) ||
        v.barcodes.some(b => b.additionalCodes?.includes(additionalCode))
      )
    );
    
    if (barcodeExists) {
      setError('هذا الباركود موجود بالفعل في أحد المتغيرات');
      return;
    }
    
    const updatedVariants = [...variants];
    const currentBarcode = updatedVariants[variantIndex].barcodes[barcodeIndex];
    
    // إنشاء مصفوفة باركودات إضافية إذا لم تكن موجودة
    if (!currentBarcode.additionalCodes) {
      currentBarcode.additionalCodes = [];
    }
    
    // إضافة الباركود الإضافي
    currentBarcode.additionalCodes.push(additionalCode);
    
    // تحديث الحالة
    setVariants(updatedVariants);
    
    // مسح حقل الإدخال للباركود الإضافي المعين
    const newAdditionalBarcodesMap = {...additionalBarcodes};
    newAdditionalBarcodesMap[`${variantIndex}-${barcodeIndex}`] = '';
    setAdditionalBarcodes(newAdditionalBarcodesMap);
    
    setError(null);
  };
  
  // حذف باركود إضافي
  const handleRemoveAdditionalBarcode = (
    variantIndex: number,
    barcodeIndex: number,
    additionalCodeIndex: number
  ) => {
    const updatedVariants = [...variants];
    const currentBarcode = updatedVariants[variantIndex].barcodes[barcodeIndex];
    
    if (currentBarcode.additionalCodes) {
      currentBarcode.additionalCodes.splice(additionalCodeIndex, 1);
      setVariants(updatedVariants);
    }
  };
  
  // Barcode handlers
  const handleAddVariantBarcode = (variantIndex: number) => {
    if (!newBarcode.trim()) {
      setError('يرجى إدخال رمز الباركود');
      return;
    }
    
    // Create a copy of the variants array
    let updatedVariants = [...variants];
    
    // If the variant doesn't exist yet or variants array is empty, create a new one
    if (!updatedVariants[variantIndex]) {
      const defaultUnit = units.length > 0 ? units[0].name : 'قطعة';
      const newVariant: ProductVariant = {
        serialNumber: variantIndex + 1, // إضافة الرقم التسلسلي
        attributes: {},
        quantity: 0,
        unit: defaultUnit,
        code: '',
        barcodes: [],
        status: 'active'
      };
      
      // Add the new variant to the array
      updatedVariants[variantIndex] = newVariant;
    }
    
    // Now we know the variant exists
    const variant = updatedVariants[variantIndex];
    
    // Make sure we have a valid unit
    const defaultUnit = variant.unit || (units.length > 0 ? units[0].name : 'قطعة');
    
    // تقسيم النص المدخل إلى عدة باركودات (المفصولة بمسافة أو فاصلة أو سطر جديد)
    const barcodeList = newBarcode
      .split(/[\s,\n]+/) // تقسيم بالمسافات أو الفواصل أو السطور الجديدة
      .filter(code => code.trim() !== ''); // استبعاد الباركودات الفارغة
    
    // التحقق من وجود باركودات مكررة
    const duplicateBarcodes: string[] = [];
    const newBarcodes: ProductBarcode[] = [];
    
    // معالجة كل باركود في القائمة
    for (const code of barcodeList) {
      // التحقق مما إذا كان الباركود موجودًا بالفعل في أي متغير
      const barcodeExists = updatedVariants.some(v =>
        v && v.barcodes && v.barcodes.some(b => b.code === code)
      );
      
      if (barcodeExists) {
        duplicateBarcodes.push(code);
      } else {
        // إنشاء كائن باركود جديد
        const newBarcodeObj: ProductBarcode = {
          code: code,
          // أول باركود فقط سيكون افتراضي إذا لم تكن هناك باركودات
          isDefault: variant.barcodes.length === 0 && newBarcodes.length === 0,
          retailPrice: 0,
          wholesalePrice: 0,
          customPrice1: 0,
          customPrice2: 0,
          unit: defaultUnit,
          categories: [],
          defaultCategory: undefined, // لا يوجد تصنيف افتراضي في البداية
          sku: variant.sku || '',
          additionalCodes: [] // إضافة مصفوفة فارغة للباركودات الإضافية
        };
        
        newBarcodes.push(newBarcodeObj);
      }
    }
    
    // إظهار خطأ إذا كانت هناك باركودات مكررة
    if (duplicateBarcodes.length > 0) {
      setError(`الباركودات التالية موجودة بالفعل: ${duplicateBarcodes.join(', ')}`);
      
      // إذا كانت هناك باركودات جديدة صحيحة، نضيفها على أي حال
      if (newBarcodes.length > 0) {
        updatedVariants[variantIndex].barcodes.push(...newBarcodes);
        setVariants(updatedVariants);
        setNewBarcode('');
      }
      
      return;
    }
    
    // إضافة الباركودات الجديدة للمتغير
    if (newBarcodes.length > 0) {
      updatedVariants[variantIndex].barcodes.push(...newBarcodes);
      setVariants(updatedVariants);
      setNewBarcode('');
      setError(null);
      
      // عرض رسالة نجاح إذا تم إضافة أكثر من باركود
      if (newBarcodes.length > 1) {
        setSuccess(`تم إضافة ${newBarcodes.length} باركود بنجاح`);
        setTimeout(() => setSuccess(null), 3000);
      }
    }
  };

  // Handle updating barcode categories
  const handleUpdateBarcodeCategories = (
    variantIndex: number,
    barcodeIndex: number,
    selectedCategoryIds: string[]
  ) => {
    console.log(`تحديث تصنيفات الباركود: المتغير ${variantIndex}, الباركود ${barcodeIndex}, التصنيفات:`, selectedCategoryIds);
    
    const updatedVariants = [...variants];
    
    // تحديث تصنيفات الباركود
    updatedVariants[variantIndex].barcodes[barcodeIndex].categories = selectedCategoryIds;
    
    // تأكد من أن تصنيفات المتغير تحتوي على جميع تصنيفات الباركودات
    const allBarcodeCategories = new Set<string>();
    
    // جمع جميع التصنيفات من كل الباركودات
    updatedVariants[variantIndex].barcodes.forEach(barcode => {
      if (barcode.categories && Array.isArray(barcode.categories)) {
        barcode.categories.forEach(categoryId => {
          if (categoryId) allBarcodeCategories.add(categoryId.toString());
        });
      }
    });
    
    // تحديث تصنيفات المتغير لتضمن جميع تصنيفات الباركودات
    updatedVariants[variantIndex].categories = Array.from(allBarcodeCategories);
    
    console.log(`تصنيفات المتغير بعد التحديث:`, updatedVariants[variantIndex].categories);
    
    setVariants(updatedVariants);
  };

  // Handle setting default category for a barcode
  const handleSetDefaultCategory = (
    variantIndex: number,
    barcodeIndex: number,
    categoryId: string
  ) => {
    const updatedVariants = [...variants];
    const barcode = updatedVariants[variantIndex].barcodes[barcodeIndex];
    
    // If the category is not in the barcode's categories, add it first
    if (!barcode.categories || !barcode.categories.includes(categoryId)) {
      if (!barcode.categories) {
        barcode.categories = [];
      }
      barcode.categories.push(categoryId);
    }
    
    // Set this category as the default (only one can be default)
    barcode.defaultCategory = barcode.defaultCategory === categoryId ? undefined : categoryId;
    
    setVariants(updatedVariants);
  };
  
  const handleUpdateVariantBarcode = (
    variantIndex: number,
    barcodeIndex: number,
    field: keyof ProductBarcode,
    value: any
  ) => {
    const updatedVariants = [...variants];
    const barcodes = [...updatedVariants[variantIndex].barcodes];
    
    if (field === 'isDefault' && value === true) {
      // Reset all other isDefault values to false
      barcodes.forEach(barcode => (barcode.isDefault = false));
    }
    
    (barcodes[barcodeIndex] as any)[field] = value;
    updatedVariants[variantIndex].barcodes = barcodes;
    
    // If updating the SKU field on a default barcode, also update the variant's SKU
    if (field === 'sku' && barcodes[barcodeIndex].isDefault) {
      updatedVariants[variantIndex].sku = value;
    }
    
    setVariants(updatedVariants);
  };
  
  const handleRemoveVariantBarcode = (variantIndex: number, barcodeIndex: number) => {
    const updatedVariants = [...variants];
    updatedVariants[variantIndex].barcodes.splice(barcodeIndex, 1);
    
    // If we removed the default barcode and there are other barcodes, set the first one as default
    if (updatedVariants[variantIndex].barcodes.length > 0 &&
        !updatedVariants[variantIndex].barcodes.some(b => b.isDefault)) {
      updatedVariants[variantIndex].barcodes[0].isDefault = true;
    }
    
    setVariants(updatedVariants);
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedVariant(isExpanded ? panel : false);
  };

  // Cost History handlers
  const handleOpenCostHistory = (barcode: ProductBarcode) => {
    setCostHistoryDialog({
      open: true,
      costHistory: barcode.costHistory || [],
      barcodeCode: barcode.code
    });
  };

  const handleCloseCostHistory = () => {
    setCostHistoryDialog({
      open: false,
      costHistory: [],
      barcodeCode: ''
    });
  };

  // Debounced form saving to prevent rapid successive submissions
  const debouncedSubmit = useCallback(
    debounce(async (productData: Omit<Product, '_id'>, isEdit: boolean, productId?: string) => {
      try {
        if (isEdit && productId) {
          await updateProduct(productId, productData);
          setSuccess('تم تحديث المنتج بنجاح');
        } else {
          await createProduct(productData);
          setSuccess('تم إضافة المنتج بنجاح');
          
          // Reset form for new product
          if (!isEdit) {
            setName('');
            setDescription('');
            setStatus('active');
            setMainImage('');
            setImages([]);
            setAttributes([]);
            setVariants([]);
          }
        }
        
        setError(null);
      } catch (err: any) {
        console.error('Error saving product:', err);
        
        // معالجة أخطاء الباركود المكرر
        if (err.type === 'barcode_duplicate' || err.type === 'barcode_validation') {
          if (err.details && Array.isArray(err.details)) {
            // عرض جميع أخطاء الباركود
            const errorMessages = err.details.join('\n• ');
            setError(`أخطاء في الباركود:\n• ${errorMessages}`);
          } else {
            setError(err.message || 'يوجد باركود مكرر. يرجى التحقق من أرقام الباركود.');
          }
        }
        // معالجة الأخطاء العامة
        else if (err?.response?.data?.error) {
          setError(err.response.data.error);
        }
        else if (err?.response?.data?.message) {
          setError(err.response.data.message);
        }
        else if (err.message) {
          setError(err.message);
        }
        else {
          setError('حدث خطأ أثناء حفظ المنتج');
        }
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Save product
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Don't allow form submission if we're already loading
    if (loading) {
      return;
    }
    
    if (!name.trim()) {
      setError('اسم المنتج مطلوب');
      return;
    }

    // Validate variants have required fields
    const invalidVariants = variants.filter(variant =>
      !variant.unit ||
      variant.barcodes.some(barcode => !barcode.code || !barcode.unit || barcode.retailPrice < 0)
    );

    if (invalidVariants.length > 0) {
      setError('يرجى التأكد من إدخال جميع الحقول المطلوبة للمتغيرات (الباركود، سعر التجزئة، وحدة القياس)');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Sanitize product data to remove any potential issues
      const sanitizedVariants = variants.map(variant => {
        // Find the default barcode to ensure SKU consistency
        const defaultBarcode = variant.barcodes.find(b => b.isDefault);
        
        // If variant has no SKU but default barcode does, use that
        const variantSku = variant.sku || (defaultBarcode?.sku || '');
        
        return {
          ...variant,
          // Ensure variant has the SKU from default barcode if not already set
          sku: variantSku,
          // Ensure barcodes are valid
          barcodes: variant.barcodes.map(barcode => ({
            ...barcode,
            retailPrice: Number(barcode.retailPrice) || 0,
            wholesalePrice: Number(barcode.wholesalePrice) || 0,
            customPrice1: Number(barcode.customPrice1) || 0,
            customPrice2: Number(barcode.customPrice2) || 0,
            unit: barcode.unit || variant.unit || 'قطعة',
            // Ensure SKU is consistent with variant for default barcode
            sku: barcode.isDefault ? variantSku : (barcode.sku || ''),
            // Ensure categories is an array
            categories: Array.isArray(barcode.categories) ? barcode.categories : [],
            // Ensure defaultCategory is preserved
            defaultCategory: barcode.defaultCategory || undefined
          })),
          // Ensure status is valid
          status: ['active', 'inactive', 'outOfStock'].includes(variant.status) ? variant.status : 'active',
          // Ensure quantity is a number
          quantity: Number(variant.quantity) || 0
        };
      });

      const productData: Omit<Product, '_id'> = {
        name,
        description,
        attributeNames: attributes,
        variants: sanitizedVariants,
        status,
        mainImage: mainImage || undefined,
        images: images.length > 0 ? images : undefined,
        productType: isVariable ? 'variable' : 'simple'
      };

      // Use the debounced submit function
      debouncedSubmit(productData, isEditing, id);
      
      setError(null);
    } catch (err: any) {
      // Get more detailed error message if available
      if (err?.response?.data?.message) {
        setError(`حدث خطأ أثناء حفظ المنتج: ${err.response.data.message}`);
      } else {
        setError('حدث خطأ أثناء حفظ المنتج');
      }
      console.error('Error saving product:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, direction: 'rtl', textAlign: 'left' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ textAlign: 'left' }}>
          {isEditing ? 'تعديل منتج' : 'إضافة منتج جديد'}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/products')}
            sx={{ mr: 1 }}
          >
            عودة
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'جاري الحفظ...' : 'حفظ المنتج'}
          </Button>
        </Box>
      </Box>
{error && (
  <Alert severity="error" sx={{ mb: 3 }}>
    {error}
  </Alert>
)}

{/* Show loading indicator for initial data loading */}
{loadingInitialData && (
  <Alert severity="info" sx={{ mb: 3 }}>
    جاري تحميل البيانات الضرورية...
  </Alert>
)}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
          معلومات المنتج الأساسية
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="اسم المنتج"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>حالة المنتج</InputLabel>
              <Select
                value={status}
                label="حالة المنتج"
                onChange={(e) => setStatus(e.target.value as any)}
              >
                <MenuItem value="active">نشط</MenuItem>
                <MenuItem value="inactive">غير نشط</MenuItem>
                <MenuItem value="discontinued">متوقف</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>نوع المنتج</InputLabel>
              <Select
                value={isVariable ? 'variable' : 'simple'}
                label="نوع المنتج"
                onChange={(e) => setIsVariable(e.target.value === 'variable')}
              >
                <MenuItem value="variable">منتج متغير (متعدد الخيارات)</MenuItem>
                <MenuItem value="simple">منتج بسيط (غير متغير)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="وصف المنتج"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
            />
          </Grid>
        </Grid>
      </Paper>

      {isVariable && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
            خصائص المنتج
          </Typography>
          
          {/* إضافة خاصية من الخصائص المحفوظة */}
          {savedAttributes.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                إضافة من الخصائص المحفوظة
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth>
                    <InputLabel>اختر خاصية محفوظة</InputLabel>
                    <Select
                      value=""
                      label="اختر خاصية محفوظة"
                      onChange={(e) => {
                        const selectedAttribute = savedAttributes.find(attr => attr._id === e.target.value);
                        if (selectedAttribute) {
                          // Check if attribute already exists
                          if (attributes.some(attr => attr.name === selectedAttribute.name)) {
                            setError('هذه الخاصية موجودة بالفعل');
                            return;
                          }
                          // Add the saved attribute to the product
                          setAttributes([...attributes, {
                            name: selectedAttribute.name,
                            values: [...selectedAttribute.values]
                          }]);
                          setError(null);
                          setSuccess('تمت إضافة الخاصية بنجاح');
                          setTimeout(() => setSuccess(null), 3000);
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>-- اختر خاصية --</em>
                      </MenuItem>
                      {savedAttributes.map((attr) => (
                        <MenuItem key={attr._id} value={attr._id}>
                          {attr.name} ({attr.values.length} قيم)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Typography variant="body2" color="textSecondary">
                    اختر من الخصائص المحفوظة لإضافتها إلى المنتج. يمكنك إدارة الخصائص من صفحة "خصائص المنتج".
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* إضافة خاصية يدوياً */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'left', fontWeight: 'bold' }}>
              إضافة خاصية جديدة
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="اسم الخاصية"
                  placeholder="مثال: اللون، المقاس، المادة"
                  value={newAttributeName}
                  onChange={(e) => setNewAttributeName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="قيمة (اختياري)"
                  placeholder="مثال: أحمر، كبير، قطن"
                  value={newAttributeValue}
                  onChange={(e) => setNewAttributeValue(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAttribute}
                >
                  إضافة
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {attributes.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'left', py: 2 }}>
              لا توجد خصائص بعد. أضف خصائص لتحديد متغيرات المنتج.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {attributes.map((attribute, attrIndex) => (
                <Grid item xs={12} key={attrIndex}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {attribute.name}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAttribute(attrIndex)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {attribute.values.map((value, valueIndex) => (
                        <Chip
                          key={valueIndex}
                          label={value}
                          onDelete={() => handleRemoveAttributeValue(attrIndex, valueIndex)}
                        />
                      ))}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        label="قيمة جديدة"
                        placeholder="أضف قيمة جديدة"
                        sx={{ flexGrow: 1 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddAttributeValue(attrIndex, (e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          try {
                            const input = e.currentTarget.previousSibling as HTMLInputElement;
                            if (input && typeof input.value === 'string') {
                              handleAddAttributeValue(attrIndex, input.value);
                              input.value = '';
                            } else {
                              setError('حدث خطأ في إضافة القيمة، يرجى المحاولة مرة أخرى');
                            }
                          } catch (error) {
                            console.error('Error adding attribute value:', error);
                            setError('حدث خطأ في إضافة القيمة');
                          }
                        }}
                      >
                        إضافة
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {isVariable ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ textAlign: 'left' }}>متغيرات المنتج</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVariant}
            >
              إضافة متغير
            </Button>
          </Box>

          {variants.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'left', py: 2 }}>
              لا توجد متغيرات بعد. أضف متغيرات للمنتج لإدارة المخزون.
            </Typography>
          ) : (
            <Box>
              {variants.map((variant, variantIndex) => (
                <Accordion
                  key={variantIndex}
                  expanded={expandedVariant === `variant-${variantIndex}`}
                  onChange={handleAccordionChange(`variant-${variantIndex}`)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'background.default' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography>
                        متغير {variant.serialNumber || variantIndex + 1}:{' '}
                        {Object.entries(variant.attributes)
                          .map(([key, value]) => `${key}: ${value}`)
                          .join(', ') || 'جديد'}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVariant(variantIndex);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'left' }}>
                          خصائص المتغير
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {attributes.map((attr, attrIndex) => (
                            <FormControl key={attrIndex} sx={{ minWidth: 200, flex: 1 }}>
                              <InputLabel>{attr.name}</InputLabel>
                              <Select
                                value={variant.attributes[attr.name] || ''}
                                label={attr.name}
                                onChange={(e) =>
                                  handleUpdateVariantAttribute(
                                    variantIndex,
                                    attr.name,
                                    e.target.value
                                  )
                                }
                              >
                                <MenuItem value="">
                                  <em>-- اختر {attr.name} --</em>
                                </MenuItem>
                                {attr.values.map((value, valueIndex) => (
                                  <MenuItem key={valueIndex} value={value}>
                                    {value}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ))}
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ textAlign: 'left' }}>الباركودات</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              label="باركود جديد"
                              value={newBarcode}
                              onChange={(e) => setNewBarcode(e.target.value)}
                              placeholder="أدخل رمز الباركود أو عدة باركودات مفصولة بمسافة أو فاصلة"
                              multiline
                              maxRows={3}
                              sx={{ minWidth: 300 }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddVariantBarcode(variantIndex)}
                            >
                              إضافة
                            </Button>
                          </Box>
                        </Box>

                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="left">رمز الباركود</TableCell>
                                <TableCell align="left">رمز المنتج (SKU)</TableCell>
                                <TableCell align="left">سعر التجزئة</TableCell>
                                <TableCell align="left">سعر الجملة</TableCell>
                                <TableCell align="left">سعر مخصص 1</TableCell>
                                <TableCell align="left">سعر مخصص 2</TableCell>
                                <TableCell align="left">آخر تكلفة</TableCell>
                                <TableCell align="left">متوسط التكلفة</TableCell>
                                <TableCell align="left">وحدة القياس</TableCell>
                                <TableCell align="left">التصنيفات</TableCell>
                                <TableCell align="left">الباركود الافتراضي</TableCell>
                                <TableCell align="left">إجراءات</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {variant.barcodes.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={12} align="left">
                                    لا توجد باركودات بعد
                                  </TableCell>
                                </TableRow>
                              ) : (
                                variant.barcodes.map((barcode, barcodeIndex) => (
                                  <TableRow key={barcodeIndex}>
                                    <TableCell>
                                      <Box>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          value={barcode.code}
                                          onChange={(e) =>
                                            handleUpdateVariantBarcode(
                                              variantIndex,
                                              barcodeIndex,
                                              'code',
                                              e.target.value
                                            )
                                          }
                                        />
                                        
                                        {/* عرض الباركودات الإضافية */}
                                        {barcode.additionalCodes && barcode.additionalCodes.length > 0 && (
                                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {barcode.additionalCodes.map((additionalCode, additionalIndex) => (
                                              <Chip
                                                key={additionalIndex}
                                                label={additionalCode}
                                                size="small"
                                                onDelete={() => handleRemoveAdditionalBarcode(variantIndex, barcodeIndex, additionalIndex)}
                                                sx={{ direction: 'ltr' }}
                                              />
                                            ))}
                                          </Box>
                                        )}
                                        
                                        {/* إضافة باركود إضافي */}
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                                          <TextField
                                            size="small"
                                            placeholder="باركود إضافي"
                                            variant="outlined"
                                            value={additionalBarcodes[`${variantIndex}-${barcodeIndex}`] || ''}
                                            onChange={(e) => {
                                              const newAdditionalBarcodesMap = {...additionalBarcodes};
                                              newAdditionalBarcodesMap[`${variantIndex}-${barcodeIndex}`] = e.target.value;
                                              setAdditionalBarcodes(newAdditionalBarcodesMap);
                                            }}
                                            onKeyDown={(e) => {
                                              const currentValue = additionalBarcodes[`${variantIndex}-${barcodeIndex}`] || '';
                                              if (e.key === 'Enter' && currentValue.trim()) {
                                                e.preventDefault();
                                                handleAddAdditionalBarcode(variantIndex, barcodeIndex, currentValue);
                                                // مسح الحقل بعد الإضافة
                                                const newAdditionalBarcodesMap = {...additionalBarcodes};
                                                newAdditionalBarcodesMap[`${variantIndex}-${barcodeIndex}`] = '';
                                                setAdditionalBarcodes(newAdditionalBarcodesMap);
                                              }
                                            }}
                                            sx={{ maxWidth: '70%' }}
                                          />
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                              const currentValue = additionalBarcodes[`${variantIndex}-${barcodeIndex}`] || '';
                                              if (currentValue.trim()) {
                                                handleAddAdditionalBarcode(variantIndex, barcodeIndex, currentValue);
                                                // مسح الحقل بعد الإضافة
                                                const newAdditionalBarcodesMap = {...additionalBarcodes};
                                                newAdditionalBarcodesMap[`${variantIndex}-${barcodeIndex}`] = '';
                                                setAdditionalBarcodes(newAdditionalBarcodesMap);
                                              }
                                            }}
                                            disabled={!(additionalBarcodes[`${variantIndex}-${barcodeIndex}`] || '').trim()}
                                          >
                                            <AddIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <TextField
                                        InputProps={{ sx: { direction: 'ltr' } }}
                                        fullWidth
                                        size="small"
                                        value={barcode.sku || ''}
                                        placeholder="رمز تعريفي للمنتج"
                                        onChange={(e) =>
                                          handleUpdateVariantBarcode(
                                            variantIndex,
                                            barcodeIndex,
                                            'sku',
                                            e.target.value
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={barcode.retailPrice}
                                        onChange={(e) =>
                                          handleUpdateVariantBarcode(
                                            variantIndex,
                                            barcodeIndex,
                                            'retailPrice',
                                            Number(e.target.value)
                                          )
                                        }
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={barcode.wholesalePrice}
                                        onChange={(e) =>
                                          handleUpdateVariantBarcode(
                                            variantIndex,
                                            barcodeIndex,
                                            'wholesalePrice',
                                            Number(e.target.value)
                                          )
                                        }
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={barcode.customPrice1}
                                        onChange={(e) =>
                                          handleUpdateVariantBarcode(
                                            variantIndex,
                                            barcodeIndex,
                                            'customPrice1',
                                            Number(e.target.value)
                                          )
                                        }
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={barcode.customPrice2}
                                        onChange={(e) =>
                                          handleUpdateVariantBarcode(
                                            variantIndex,
                                            barcodeIndex,
                                            'customPrice2',
                                            Number(e.target.value)
                                          )
                                        }
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={barcode.lastCost || 0}
                                        onChange={(e) =>
                                          handleUpdateVariantBarcode(
                                            variantIndex,
                                            barcodeIndex,
                                            'lastCost',
                                            Number(e.target.value)
                                          )
                                        }
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                        placeholder="آخر تكلفة"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          value={barcode.averageCost || 0}
                                          onChange={(e) =>
                                            handleUpdateVariantBarcode(
                                              variantIndex,
                                              barcodeIndex,
                                              'averageCost',
                                              Number(e.target.value)
                                            )
                                          }
                                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                          placeholder="متوسط التكلفة"
                                        />
                                        <Tooltip title="عرض تاريخ التكاليف">
                                          <IconButton
                                            size="small"
                                            onClick={() => handleOpenCostHistory(barcode)}
                                            disabled={!barcode.costHistory || barcode.costHistory.length === 0}
                                          >
                                            <HistoryIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>وحدة القياس</InputLabel>
                                        <Select
                                          value={barcode.unit || ''}
                                          label="وحدة القياس"
                                          onChange={(e) =>
                                            handleUpdateVariantBarcode(
                                              variantIndex,
                                              barcodeIndex,
                                              'unit',
                                              e.target.value
                                            )
                                          }
                                        >
                                          {units.map((unit) => (
                                            <MenuItem key={unit._id} value={unit.name}>
                                              {unit.name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>التصنيفات</InputLabel>
                                        <Select
                                          multiple
                                          value={barcode.categories || []}
                                          onChange={(e) => {
                                            const selectedCategoryIds = e.target.value as string[];
                                            handleUpdateBarcodeCategories(
                                              variantIndex,
                                              barcodeIndex,
                                              selectedCategoryIds
                                            );
                                          }}
                                          input={<OutlinedInput label="التصنيفات" />}
                                          renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {(selected as string[]).map((categoryId) => {
                                                const category = categories.find(cat => cat._id === categoryId);
                                                const isDefault = barcode.defaultCategory === categoryId;
                                                return (
                                                  <Chip
                                                    key={categoryId}
                                                    label={
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {category ? category.name : categoryId}
                                                        {isDefault && <span style={{ color: '#ffd700' }}>★</span>}
                                                      </Box>
                                                    }
                                                    size="small"
                                                    sx={isDefault ? {
                                                      backgroundColor: '#fff3cd',
                                                      border: '1px solid #ffd700',
                                                      '& .MuiChip-label': { fontWeight: 'bold' }
                                                    } : {}}
                                                  />
                                                );
                                              })}
                                            </Box>
                                          )}
                                        >
                                          {categories.map((category) => (
                                            <MenuItem key={category._id} value={category._id}>
                                              <Checkbox checked={(barcode.categories || []).indexOf(category._id) > -1} />
                                              <ListItemText
                                                primary={
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {category.name}
                                                    {barcode.defaultCategory === category._id && (
                                                      <span style={{ color: '#ffd700', fontSize: '16px' }}>★</span>
                                                    )}
                                                    {(barcode.categories || []).includes(category._id) && (
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleSetDefaultCategory(variantIndex, barcodeIndex, category._id);
                                                        }}
                                                        sx={{
                                                          ml: 'auto',
                                                          color: barcode.defaultCategory === category._id ? '#ffd700' : '#ccc',
                                                          '&:hover': { color: '#ffd700' }
                                                        }}
                                                      >
                                                        ★
                                                      </IconButton>
                                                    )}
                                                  </Box>
                                                }
                                              />
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      <FormControl>
                                        <Select
                                          size="small"
                                          value={barcode.isDefault ? 'yes' : 'no'}
                                          onChange={(e) =>
                                            handleUpdateVariantBarcode(
                                              variantIndex,
                                              barcodeIndex,
                                              'isDefault',
                                              e.target.value === 'yes'
                                            )
                                          }
                                        >
                                          <MenuItem value="yes">نعم</MenuItem>
                                          <MenuItem value="no">لا</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          handleRemoveVariantBarcode(variantIndex, barcodeIndex)
                                        }
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth sx={{ mt: 3 }}>
                          <InputLabel>حالة المتغير</InputLabel>
                          <Select
                            value={variant.status}
                            label="حالة المتغير"
                            onChange={(e) =>
                              handleUpdateVariantField(
                                variantIndex,
                                'status',
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="active">نشط</MenuItem>
                            <MenuItem value="inactive">غير نشط</MenuItem>
                            <MenuItem value="outOfStock">نفذت الكمية</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ textAlign: 'left' }}>الباركودات والأسعار</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt: 3 }}>
            <Typography variant="subtitle1" sx={{ textAlign: 'left' }}>الباركودات</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="باركود جديد"
                value={newBarcode}
                onChange={(e) => setNewBarcode(e.target.value)}
                placeholder="أدخل رمز الباركود أو عدة باركودات مفصولة بمسافة أو فاصلة"
                multiline
                maxRows={3}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (variants.length === 0) {
                    // إنشاء متغير افتراضي للمنتج البسيط
                    const defaultUnit = units.length > 0 ? units[0].name : '';
                    const newVariant: ProductVariant = {
                      serialNumber: 1, // إضافة الرقم التسلسلي للمتغير الافتراضي
                      attributes: {},
                      quantity: 0,
                      unit: defaultUnit,
                      code: '',
                      barcodes: [],
                      status: 'active'
                    };
                    setVariants([newVariant]);
                    handleAddVariantBarcode(0);
                  } else {
                    handleAddVariantBarcode(0);
                  }
                }}
              >
                إضافة
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">رمز الباركود</TableCell>
                  <TableCell align="left">رمز المنتج (SKU)</TableCell>
                  <TableCell align="left">سعر التجزئة</TableCell>
                  <TableCell align="left">سعر الجملة</TableCell>
                  <TableCell align="left">سعر مخصص 1</TableCell>
                  <TableCell align="left">سعر مخصص 2</TableCell>
                  <TableCell align="left">آخر تكلفة</TableCell>
                  <TableCell align="left">متوسط التكلفة</TableCell>
                  <TableCell align="left">وحدة القياس</TableCell>
                  <TableCell align="left">التصنيفات</TableCell>
                  <TableCell align="left">الباركود الافتراضي</TableCell>
                  <TableCell align="left">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.length === 0 || variants[0].barcodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="left">
                      لا توجد باركودات بعد. أضف باركود للمنتج.
                    </TableCell>
                  </TableRow>
                ) : (
                  variants[0].barcodes.map((barcode, barcodeIndex) => (
                    <TableRow key={barcodeIndex}>
                      <TableCell>
                        <Box>
                          <TextField
                            fullWidth
                            size="small"
                            value={barcode.code}
                            onChange={(e) =>
                              handleUpdateVariantBarcode(
                                0,
                                barcodeIndex,
                                'code',
                                e.target.value
                              )
                            }
                          />
                          
                          {/* عرض الباركودات الإضافية */}
                          {barcode.additionalCodes && barcode.additionalCodes.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {barcode.additionalCodes.map((additionalCode, additionalIndex) => (
                                <Chip
                                  key={additionalIndex}
                                  label={additionalCode}
                                  size="small"
                                  onDelete={() => handleRemoveAdditionalBarcode(0, barcodeIndex, additionalIndex)}
                                  sx={{ direction: 'ltr' }}
                                />
                              ))}
                            </Box>
                          )}
                          
                          {/* إضافة باركود إضافي */}
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              placeholder="باركود إضافي"
                              variant="outlined"
                              value={additionalBarcodes[`0-${barcodeIndex}`] || ''}
                              onChange={(e) => {
                                const newAdditionalBarcodesMap = {...additionalBarcodes};
                                newAdditionalBarcodesMap[`0-${barcodeIndex}`] = e.target.value;
                                setAdditionalBarcodes(newAdditionalBarcodesMap);
                              }}
                              onKeyDown={(e) => {
                                const currentValue = additionalBarcodes[`0-${barcodeIndex}`] || '';
                                if (e.key === 'Enter' && currentValue.trim()) {
                                  e.preventDefault();
                                  handleAddAdditionalBarcode(0, barcodeIndex, currentValue);
                                  // مسح الحقل بعد الإضافة
                                  const newAdditionalBarcodesMap = {...additionalBarcodes};
                                  newAdditionalBarcodesMap[`0-${barcodeIndex}`] = '';
                                  setAdditionalBarcodes(newAdditionalBarcodesMap);
                                }
                              }}
                              sx={{ maxWidth: '70%' }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                const currentValue = additionalBarcodes[`0-${barcodeIndex}`] || '';
                                if (currentValue.trim()) {
                                  handleAddAdditionalBarcode(0, barcodeIndex, currentValue);
                                  // مسح الحقل بعد الإضافة
                                  const newAdditionalBarcodesMap = {...additionalBarcodes};
                                  newAdditionalBarcodesMap[`0-${barcodeIndex}`] = '';
                                  setAdditionalBarcodes(newAdditionalBarcodesMap);
                                }
                              }}
                              disabled={!(additionalBarcodes[`0-${barcodeIndex}`] || '').trim()}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField
                          InputProps={{ sx: { direction: 'ltr' } }}
                          fullWidth
                          size="small"
                          value={barcode.sku || ''}
                          placeholder="رمز تعريفي للمنتج"
                          onChange={(e) =>
                            handleUpdateVariantBarcode(
                              0,
                              barcodeIndex,
                              'sku',
                              e.target.value
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={barcode.retailPrice}
                          onChange={(e) =>
                            handleUpdateVariantBarcode(
                              0,
                              barcodeIndex,
                              'retailPrice',
                              Number(e.target.value)
                            )
                          }
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={barcode.wholesalePrice}
                          onChange={(e) =>
                            handleUpdateVariantBarcode(
                              0,
                              barcodeIndex,
                              'wholesalePrice',
                              Number(e.target.value)
                            )
                          }
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={barcode.customPrice1}
                          onChange={(e) =>
                            handleUpdateVariantBarcode(
                              0,
                              barcodeIndex,
                              'customPrice1',
                              Number(e.target.value)
                            )
                          }
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={barcode.customPrice2}
                          onChange={(e) =>
                            handleUpdateVariantBarcode(
                              0,
                              barcodeIndex,
                              'customPrice2',
                              Number(e.target.value)
                            )
                          }
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={barcode.lastCost || 0}
                          onChange={(e) =>
                            handleUpdateVariantBarcode(
                              0,
                              barcodeIndex,
                              'lastCost',
                              Number(e.target.value)
                            )
                          }
                          InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                          placeholder="آخر تكلفة"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={barcode.averageCost || 0}
                            onChange={(e) =>
                              handleUpdateVariantBarcode(
                                0,
                                barcodeIndex,
                                'averageCost',
                                Number(e.target.value)
                              )
                            }
                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            placeholder="متوسط التكلفة"
                          />
                          <Tooltip title="عرض تاريخ التكاليف">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCostHistory(barcode)}
                              disabled={!barcode.costHistory || barcode.costHistory.length === 0}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>وحدة القياس</InputLabel>
                          <Select
                            value={barcode.unit || ''}
                            label="وحدة القياس"
                            onChange={(e) =>
                              handleUpdateVariantBarcode(
                                0,
                                barcodeIndex,
                                'unit',
                                e.target.value
                              )
                            }
                          >
                            {units.map((unit) => (
                              <MenuItem key={unit._id} value={unit.name}>
                                {unit.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>التصنيفات</InputLabel>
                          <Select
                            multiple
                            value={barcode.categories || []}
                            onChange={(e) => {
                              const selectedCategoryIds = e.target.value as string[];
                              handleUpdateBarcodeCategories(
                                0,
                                barcodeIndex,
                                selectedCategoryIds
                              );
                            }}
                            input={<OutlinedInput label="التصنيفات" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((categoryId) => {
                                  const category = categories.find(cat => cat._id === categoryId);
                                  const isDefault = barcode.defaultCategory === categoryId;
                                  return (
                                    <Chip
                                      key={categoryId}
                                      label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          {category ? category.name : categoryId}
                                          {isDefault && <span style={{ color: '#ffd700' }}>★</span>}
                                        </Box>
                                      }
                                      size="small"
                                      sx={isDefault ? {
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffd700',
                                        '& .MuiChip-label': { fontWeight: 'bold' }
                                      } : {}}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {categories.map((category) => (
                              <MenuItem key={category._id} value={category._id}>
                                <Checkbox checked={(barcode.categories || []).indexOf(category._id) > -1} />
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {category.name}
                                      {barcode.defaultCategory === category._id && (
                                        <span style={{ color: '#ffd700', fontSize: '16px' }}>★</span>
                                      )}
                                      {(barcode.categories || []).includes(category._id) && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSetDefaultCategory(0, barcodeIndex, category._id);
                                          }}
                                          sx={{
                                            ml: 'auto',
                                            color: barcode.defaultCategory === category._id ? '#ffd700' : '#ccc',
                                            '&:hover': { color: '#ffd700' }
                                          }}
                                        >
                                          ★
                                        </IconButton>
                                      )}
                                    </Box>
                                  }
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl>
                          <Select
                            size="small"
                            value={barcode.isDefault ? 'yes' : 'no'}
                            onChange={(e) =>
                              handleUpdateVariantBarcode(
                                0,
                                barcodeIndex,
                                'isDefault',
                                e.target.value === 'yes'
                              )
                            }
                          >
                            <MenuItem value="yes">نعم</MenuItem>
                            <MenuItem value="no">لا</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleRemoveVariantBarcode(0, barcodeIndex)
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'جاري الحفظ...' : 'حفظ المنتج'}
        </Button>
      </Box>

      {/* Cost History Dialog */}
      <Dialog
        open={costHistoryDialog.open}
        onClose={handleCloseCostHistory}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          تاريخ التكاليف - الباركود: {costHistoryDialog.barcodeCode}
        </DialogTitle>
        <DialogContent>
          {costHistoryDialog.costHistory.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
              لا يوجد تاريخ تكاليف لهذا الباركود
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">التاريخ</TableCell>
                    <TableCell align="left">التكلفة</TableCell>
                    <TableCell align="left">رقم الفاتورة</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {costHistoryDialog.costHistory.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {new Date(record.date).toLocaleDateString('ar-SA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {record.cost.toFixed(2)} ر.س
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {record.invoiceNumber || 'غير محدد'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCostHistory} color="primary">
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductForm;